import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FileText, Plus, Search, Filter, ClipboardList, FileSpreadsheet, Sparkles, Calculator } from 'lucide-react';
import GlassPanel from '../components/GlassPanel';
import EmptyState from '../components/EmptyState';
import { AIButton, AIBadge, AIExplanationBox } from '../components/AIComponents';

// Quote Life sub-navigation
const quoteSubNav = [
  { path: '/quotes', label: 'Quotes', icon: ClipboardList },
  { path: '/quotes/estimates', label: 'Estimates', icon: FileText },
  { path: '/quotes/proposals', label: 'Proposals', icon: FileSpreadsheet },
];

const mockEstimates = [
  { id: 101, client: 'New Client A', title: 'Office Renovation', amount: 15000, status: 'draft', date: '2024-01-15' },
  { id: 102, client: 'New Client B', title: 'Warehouse Fit-out', amount: 28500, status: 'sent', date: '2024-01-14' },
  { id: 103, client: 'Previous Client', title: 'Extension Build', amount: 45000, status: 'converted', date: '2024-01-10' },
];

export function Estimates() {
  const location = useLocation();

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Estimates</h1>
          <p>Create and manage project estimates within Quote Life</p>
        </div>
        <div className="page-header-actions">
          <button className="btn btn-primary" disabled><Plus size={16} />New Estimate</button>
        </div>
      </div>

      {/* Sub Navigation - Estimates inside Quote Life */}
      <div className="flex gap-sm mb-xl">
        {quoteSubNav.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`btn ${location.pathname === item.path ? 'btn-primary' : 'btn-secondary'}`}
            style={{ gap: 'var(--space-sm)' }}
          >
            <item.icon size={16} />
            {item.label}
          </Link>
        ))}
      </div>

      <div className="responsive-grid-3" style={{ marginBottom: 'var(--space-xl)' }}>
        <GlassPanel>
          <div style={{ fontSize: '1.75rem', fontWeight: '700', color: 'var(--text-muted)' }}>{mockEstimates.filter(e => e.status === 'draft').length}</div>
          <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>Drafts</div>
        </GlassPanel>
        <GlassPanel>
          <div style={{ fontSize: '1.75rem', fontWeight: '700', color: 'var(--status-warning)' }}>{mockEstimates.filter(e => e.status === 'sent').length}</div>
          <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>Sent</div>
        </GlassPanel>
        <GlassPanel>
          <div style={{ fontSize: '1.75rem', fontWeight: '700', color: 'var(--status-success)' }}>{mockEstimates.filter(e => e.status === 'converted').length}</div>
          <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>Converted to Quote</div>
        </GlassPanel>
      </div>

      <div style={{ display: 'flex', gap: 'var(--space-md)', marginBottom: 'var(--space-lg)' }}>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', padding: 'var(--space-sm) var(--space-md)', background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', borderRadius: 'var(--radius-md)' }}>
          <Search size={16} style={{ color: 'var(--text-dimmed)' }} />
          <input type="text" placeholder="Search estimates..." style={{ flex: 1, background: 'transparent', border: 'none', color: 'var(--text-primary)', outline: 'none' }} />
        </div>
        <button className="btn btn-secondary"><Filter size={16} />Filter</button>
      </div>

      <GlassPanel title="All Estimates" titleIcon={FileText}>
        {mockEstimates.length > 0 ? (
          <div className="list-container">
            {mockEstimates.map((estimate) => (
              <div key={estimate.id} className="glass-panel" style={{ marginBottom: 'var(--space-sm)', padding: 'var(--space-md)' }}>
                <Link to={`/quotes/estimates/${estimate.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div className="list-item-content">
                      <div className="list-item-title" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                        Estimate #{estimate.id} - {estimate.title}
                        <AIBadge text="AI" />
                      </div>
                      <div className="list-item-subtitle">{estimate.client} • {estimate.date}</div>
                    </div>
                    <div className="list-item-meta">
                      <span className={`status-badge ${estimate.status === 'converted' ? 'completed' : estimate.status === 'sent' ? 'pending' : 'draft'}`}>{estimate.status}</span>
                      <span className="list-item-amount">£{estimate.amount.toLocaleString()}</span>
                    </div>
                  </div>
                </Link>
                
                {/* AI Actions per estimate */}
                <div className="quote-card-ai-actions">
                  <AIButton icon={Sparkles} tooltip="AI generation will be enabled soon">
                    Convert estimate to quote (AI assisted)
                  </AIButton>
                  <AIButton icon={Calculator} tooltip="AI generation will be enabled soon">
                    Check pricing realism
                  </AIButton>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState icon={FileText} title="No estimates yet" description="Create your first estimate to get started" />
        )}
      </GlassPanel>

      <AIExplanationBox>
        AI can help convert estimates to formal quotes and check if your pricing is competitive based on market data.
      </AIExplanationBox>
    </div>
  );
}

export default Estimates;
